import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  ArrowRightIcon,
  AssetBadge,
  Button,
  Card,
  NetworkBadge,
  SendIcon,
  TabBar,
  TextField,
} from '../components/ui';
// Canonical token submitted to /v1/transfers as `source_token` — the
// per-chain symbol the API stamps on the row. NOTE it's a *label*, not
// a holdings claim: the worker reads the EOA's real balances and the
// pre-swap picks whichever stable it needs. What the canonical token
// IS, though, is the pipeline's no-swap input — USDC.e for PM
// allocation (wrap input on Polygon), native USDC for HL allocation
// (Bridge2 deposit on Arbitrum; CCTP burn input on Polygon).
const CANONICAL_TOKEN_BY_CHAIN: Record<'polygon' | 'arbitrum', string> = {
  polygon: 'USDC.e',
  arbitrum: 'USDC',
};
// Every stable that can live on the EOA on Polygon. pUSD is NOT here:
// on the EOA it's stranded (no allocate pipeline consumes pUSD as a
// source — see the lane gotchas). Used to derive the "other stables"
// the Polygon-source pre-swap can convert into a given target.
const POLYGON_EOA_STABLES = ['USDC.e', 'USDC', 'USDT'] as const;
// On Arbitrum the EOA_TO_{PM,HL} pipelines have no swap leg — PM burns
// native USDC via CCTP, HL deposits native USDC to Bridge2 — so only
// native USDC counts; USDT there is unusable for allocation.
function preSwapSourceStables(
  chain: 'polygon' | 'arbitrum',
  targetSymbol: string,
): readonly string[] {
  if (chain === 'arbitrum') return [];
  return POLYGON_EOA_STABLES.filter((s) => s !== targetSymbol);
}
import {
  allocateToPm,
  deallocateFromPm,
  useMyBalances,
  usePlatformConfig,
  useTransferQuote,
  useTransfers,
} from '../hooks/useApiQuery';
import { floorToCents } from '../lib/format';
import { tg } from '../lib/tg';
import type {
  BalancesChainBalance,
  MyBalancesResponse,
  TransferDirection,
  TransferRowItem,
} from '../lib/types';

// Mirrors workers/internal_transfer_processor/.../steps/hl_withdrawing.py
// and apps/api/.../transfers.py — Hyperliquid skims $1 USDC off every
// L1 withdrawal, so HL→* sizing has to budget for it.
const HL_WITHDRAW_FEE_USDC = 1;

type Endpoint = 'main' | 'pm' | 'hl';
type Chain = 'polygon' | 'arbitrum';

// HL is suppressed from the endpoint picker while Arbitrum is disabled
// — the HL bot collateral path runs through Bridge2 on Arbitrum, so
// new transfers to/from HL would fail at the API gate. Existing HL
// row history still renders because it reads ``TransferRowItem.direction``,
// not this list.
const ENDPOINTS: readonly Endpoint[] = ['main', 'pm'];

export interface TransferBreakdown {
  /** Fee paid to HL on top of the requested amount. $1 for any
   *  HL-sourced flow (hl_to_pm, hl_to_eoa, deallocate-hl), 0 elsewhere. */
  fee: number;
  /** What lands on the destination — equals the user-entered amount. */
  lands: number;
  /** What's debited from the source service: ``lands + fee``. */
  debit: number;
}

/** Pure breakdown of a transfer for preview rendering + submit-guard
 *  math. Direction-aware so the cross-bot tests catch fee-on-the-wrong-
 *  side regressions. */
export function transferBreakdown(
  direction: TransferDirection,
  amount: number,
): TransferBreakdown {
  const fee = direction === 'hl_to_pm' ? HL_WITHDRAW_FEE_USDC : 0;
  const lands = amount;
  return { fee, lands, debit: lands + fee };
}

// The EOA_TO_PM Arbitrum-source pipeline burns native USDC via CCTP,
// mints it on Polygon, then swaps native USDC → USDC.e (the wrap
// input) before delivering `amount` USDC.e to the deposit wallet. The
// swap eats up to the worker's 1% buffer + 1 raw unit — mirrors
// withdrawal_processor eoa_path's PRE_SWAP_BUFFER_BPS=100 /
// _MIN_PRE_SWAP_BUFFER_RAW=1 — so the burn is sized at
// ceil(amount·1.01) + 1 raw. USDC / USDC.e have 6 decimals.
const USDC_RAW = 1_000_000;

/** Native USDC the Arbitrum-source allocate-pm path must burn to
 *  deliver `amountUsd` USDC.e onto the PM deposit wallet. */
export function arbUsdcRequiredForPm(amountUsd: number): number {
  const amtRaw = Math.round(amountUsd * USDC_RAW);
  return (Math.ceil((amtRaw * 10_100) / 10_000) + 1) / USDC_RAW;
}

/** Inverse of {@link arbUsdcRequiredForPm}: the largest amount
 *  allocatable to PM from a given Arbitrum native-USDC balance. Also
 *  the generic inverse of the pre-swap buffer — every EOA_TO_{PM,HL}
 *  pipeline that pre-swaps a non-target stable eats the same 1% + 1-raw
 *  haircut, so this doubles as "max output you can get out of a swap
 *  source of size `b`". */
export function maxPmFromArbUsdc(arbUsdcBalance: number): number {
  const arbRaw = Math.round(arbUsdcBalance * USDC_RAW);
  return Math.max(0, Math.floor(((arbRaw - 1) * 10_000) / 10_100)) / USDC_RAW;
}

/** Largest amount allocatable from the main wallet to a bot, given the
 *  EOA's per-token balances on the source chain.
 *
 *  The EOA_TO_{PM,HL} pipelines deliver `targetBalance` of the chain's
 *  canonical token directly (no swap), and `plan_pre_swap` can pre-swap
 *  AT MOST ONE other stable into it — it picks a single source, there
 *  is no multi-leg aggregation (see
 *  `internal_transfer_processor.eoa_preswap.MixedHoldingsError`). So the
 *  swappable contribution is bounded by the *single best* other stable,
 *  haircut by the 1% pre-swap buffer. Summing all stables would
 *  over-promise — the worker would reject the row at `swapping_polygon`. */
export function maxAllocViaPreSwap(
  targetBalance: number,
  otherStableBalances: readonly number[],
): number {
  const bestOther = otherStableBalances.length ? Math.max(...otherStableBalances) : 0;
  return targetBalance + maxPmFromArbUsdc(bestOther);
}

interface Props {
  embedded?: boolean;
}

export function Transfer({ embedded = false }: Props = {}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();

  const balances = useMyBalances();
  const history = useTransfers();
  const locale = i18n.language.startsWith('ru') ? 'ru-RU' : 'en-US';

  const wrapperClass = embedded
    ? 'px-5 pt-2'
    : 'mx-auto max-w-app px-5 pt-6';

  const onSettled = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['my-balances'] }),
      qc.invalidateQueries({ queryKey: ['transfers'] }),
      qc.invalidateQueries({ queryKey: ['balance'] }),
      qc.invalidateQueries({ queryKey: ['balance-hl'] }),
    ]);
  };

  return (
    <div className={wrapperClass}>
      {embedded ? null : (
        <>
          <h1 className="pb-display text-[28px] leading-tight">
            {t('transfer.title')}
          </h1>
          <p className="mt-1.5 text-[14px] leading-relaxed text-tg-hint">
            {t('transfer.subtitle')}
          </p>
        </>
      )}

      <TransferForm
        balances={balances.data}
        locale={locale}
        onSettled={onSettled}
      />

      <HistorySection items={history.data?.items ?? []} locale={locale} />
    </div>
  );
}

// ─── Unified transfer form (from/to selects) ──────────────────────

function TransferForm({
  balances,
  locale,
  onSettled,
}: {
  balances: MyBalancesResponse | undefined;
  locale: string;
  onSettled: () => Promise<void>;
}) {
  const { t } = useTranslation();

  const [from, setFrom] = useState<Endpoint>('main');
  const [to, setTo] = useState<Endpoint>('pm');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  // Funding source for the main→PM allocate: canonical stables
  // (USDC/USDC.e/USDT, pre-swap pipeline) or referral pUSD (moves
  // straight to the bankroll, no swap). Merged into this one form so
  // referral users don't see a second, confusing card.
  const [sourceToken, setSourceToken] = useState<'stable' | 'pusd'>('stable');

  const platform = usePlatformConfig();

  // Referral pUSD sitting on the EOA — only a valid source for the
  // main→PM allocate, and only when the platform flag is on.
  const pusdBalance = floorToCents(tokenBalanceOnChain(balances, 'polygon', 'pUSD'));
  const pusdSourceAvailable =
    !!platform.data?.pusd_transfer_enabled &&
    from === 'main' &&
    to === 'pm' &&
    pusdBalance > 0;
  const usePusd = pusdSourceAvailable && sourceToken === 'pusd';

  // Snap the source picker back to stables whenever pUSD stops being a
  // valid option (direction changed, balance drained, flag off). Also
  // clear the amount — it was sized against the pUSD balance and must
  // not carry over into a stablecoin transfer (wrong source_token).
  useEffect(() => {
    if (!pusdSourceAvailable && sourceToken !== 'stable') {
      setSourceToken('stable');
      setAmount('');
      setError('');
    }
  }, [pusdSourceAvailable, sourceToken]);

  // Live-quote sizing only applies to the reachable main→PM allocate
  // (Polygon) funded by stables. For every other combo (pm→main, hl) or
  // the pUSD source (no swap leg) keep the balance math.
  const liveQuoteAlloc =
    !!platform.data?.transfer_live_quote_enabled &&
    from === 'main' &&
    to === 'pm' &&
    !usePusd;
  const quote = useTransferQuote('eoa_to_pm', 137, { enabled: liveQuoteAlloc });

  // Snap "to" to a valid endpoint (≠ from) when "from" changes.
  useEffect(() => {
    if (to === from) {
      const next = ENDPOINTS.find((e) => e !== from);
      if (next) setTo(next);
    }
  }, [from, to]);

  // Max amount allocatable to a bot from the main wallet, split by
  // source chain. The EOA_TO_{PM,HL} pipelines do NOT aggregate
  // across chains, and on a single chain `plan_pre_swap` swaps at most
  // ONE other stable into the canonical token — so summing every
  // stable (the old behaviour) over-promised and the worker rejected
  // the row at `swapping_polygon` with a MixedHoldingsError.
  //
  //   * Polygon — canonical token (no swap) + the best single other
  //     stable, haircut by the pre-swap's 1% + 1-raw buffer.
  //   * Arbitrum — PM: native USDC haircut by the post-CCTP-mint
  //     swap buffer (same 1%). HL: native USDC 1:1 (Bridge2 deposit is
  //     the only hop, no swap). USDT on Arbitrum is unusable either way.
  const allocMax = useMemo(() => {
    const polygonTarget = CANONICAL_TOKEN_BY_CHAIN.polygon; // 'USDC.e'
    const arbitrumUsdc = tokenBalanceOnChain(balances, 'arbitrum', 'USDC');
    const fromPolygon = (target: string): number =>
      maxAllocViaPreSwap(
        tokenBalanceOnChain(balances, 'polygon', target),
        preSwapSourceStables('polygon', target).map((s) =>
          tokenBalanceOnChain(balances, 'polygon', s),
        ),
      );
    return {
      // PM wraps USDC.e on Polygon; on Arbitrum it burns native USDC
      // via CCTP then swaps the mint → USDC.e (1% buffer).
      pmPolygon: fromPolygon(polygonTarget),
      pmArbitrum: maxPmFromArbUsdc(arbitrumUsdc),
      // HL's Polygon path swaps everything → native USDC (the CCTP
      // burn input); its Arbitrum path deposits native USDC straight
      // into Bridge2 (1:1, no swap).
      hlPolygon: fromPolygon('USDC'),
      hlArbitrum: arbitrumUsdc,
    };
  }, [balances]);

  // Floored to cents: this number is both shown in the BalanceRow and
  // fed straight into the MAX button + the `canSubmit` guard. A raw
  // value like 11.3898 would render as 11.39, the MAX button would
  // fill 11.39, and the guard (debit ≤ sourceBalance = 11.3898) would
  // then reject it — the classic "MAX disables the submit button" bug.
  const sourceBalance = useMemo(() => {
    const raw = (() => {
      if (from === 'main') {
        // Referral pUSD source: moves 1:1 to the bankroll (no swap), so
        // the whole EOA pUSD balance is transferable.
        if (usePusd) return pusdBalance;
        // Live-quote main→PM: the server sizes the real max (swap-all,
        // ~full balance) from a QuoterV2 quote — use it verbatim instead
        // of the client-side 1% pre-swap buffer. Falls back to the buffer
        // math until the quote resolves.
        if (liveQuoteAlloc && quote.data) {
          return Number(quote.data.max_transferable_usdc);
        }
        return to === 'pm'
          ? Math.max(allocMax.pmPolygon, allocMax.pmArbitrum)
          : Math.max(allocMax.hlPolygon, allocMax.hlArbitrum);
      }
      if (from === 'pm') return Number(balances?.pm_bot_active.amount ?? 0);
      return Number(balances?.hl_bot_active.amount ?? 0);
    })();
    return floorToCents(raw);
  }, [balances, from, to, allocMax, liveQuoteAlloc, quote.data, usePusd, pusdBalance]);

  // ``ENDPOINTS`` no longer includes ``'hl'`` while Arbitrum is
  // registry-disabled — every reachable combo is main↔pm, which has
  // no HL withdrawal fee. The fee plumbing stays in
  // ``transferBreakdown`` / ``endpointSummary`` for historical row
  // rendering (a row.direction='hl_to_pm' still needs its $1 fee
  // shown in the in-transit list).
  const fee = 0;
  const amt = Number(amount || 0);
  const debit = amt + fee;
  const max = sourceBalance;

  const pmDeployed = balances?.pm_bot_active.deployed ?? true;
  const blockedByPmDeploy = from === 'pm' && !pmDeployed;

  const canSubmit = amt > 0 && debit <= sourceBalance && !blockedByPmDeploy;

  // swap-all = MAX under live quote: derived at submit from "the amount
  // still equals the currently-displayed quoted max", not a sticky flag.
  // That way a 10s quote refetch that moved the max (balance changed / rate
  // ticked) can't leave a stale swap-all tagged on a now-mismatched amount —
  // if they diverge, we fall back to the safe fixed-target transfer.
  const maxSourceSwap = liveQuoteAlloc && !!quote.data && amt === sourceBalance;

  const mutation = useMutation({
    mutationFn: async (): Promise<TransferRowItem> => {
      if (from === 'main' && to === 'pm') {
        // Referral pUSD moves as its own source_token='pUSD' row — the
        // worker skips swap+wrap since it's already the bankroll token.
        if (usePusd) {
          return allocateToPm({
            source_token: 'pUSD',
            source_chain: 'polygon',
            amount,
          });
        }
        return allocateToPm({
          source_token: CANONICAL_TOKEN_BY_CHAIN.polygon,
          source_chain: 'polygon',
          amount,
          max_source_swap: maxSourceSwap,
        });
      }
      if (from === 'pm' && to === 'main') return deallocateFromPm({ amount });
      throw new Error(`invalid transfer combo ${from} -> ${to}`);
    },
    onSuccess: async () => {
      tg.hapticSuccess();
      setAmount('');
      setError('');
      await onSettled();
    },
    onError: () => {
      // Never surface raw server error text to the user — generic copy only.
      setError(t('common.action_failed'));
    },
  });

  const submit = () => {
    setError('');
    if (!canSubmit) {
      setError(t('transfer.err_amount'));
      return;
    }
    mutation.mutate();
  };

  return (
    <Card pad="md" className="mt-6 space-y-4">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <EndpointSelect
            label={t('transfer.from_label')}
            value={from}
            onChange={(v) => {
              setFrom(v);
              setAmount('');
              setError('');
            }}
            excluded={null}
          />
        </div>
        <div
          aria-hidden
          className="flex h-[42px] w-5 shrink-0 items-center justify-center text-tg-hint"
        >
          <ArrowRightIcon size={18} />
        </div>
        <div className="flex-1">
          <EndpointSelect
            label={t('transfer.to_label')}
            value={to}
            onChange={(v) => {
              setTo(v);
              setAmount('');
              setError('');
            }}
            excluded={from}
          />
        </div>
      </div>

      {pusdSourceAvailable ? (
        <div>
          <div className="pb-eyebrow mb-1.5">{t('transfer.source_label')}</div>
          <TabBar
            fullWidth
            size="sm"
            value={sourceToken}
            onChange={(v) => {
              setSourceToken(v);
              setAmount('');
              setError('');
            }}
            options={[
              { value: 'stable' as const, label: t('transfer.src_stable') },
              { value: 'pusd' as const, label: t('transfer.src_pusd') },
            ]}
          />
        </div>
      ) : null}

      <BalanceRow
        label={
          usePusd
            ? t('transfer.available_pusd')
            : from === 'main'
              ? t('transfer.available_main_total')
              : from === 'pm'
                ? t('transfer.available_pm')
                : t('transfer.available_hl')
        }
        amount={sourceBalance}
        symbol={usePusd ? 'pUSD' : 'USDC'}
        locale={locale}
      />

      <RouteRow
        from={
          usePusd
            ? { ...endpointSummary(from, t), asset: 'pUSD' }
            : endpointSummary(from, t)
        }
        to={endpointSummary(to, t)}
      />

      {blockedByPmDeploy ? (
        <p className="pb-caption">{t('transfer.hint_pm_no_deposit_wallet')}</p>
      ) : null}

      <AmountField
        amount={amount}
        setAmount={setAmount}
        max={max}
        helper={
          usePusd
            ? t('transfer.amount_helper_pusd')
            : from === 'hl'
              ? t('transfer.amount_helper_hl', { fee: fee.toFixed(0) })
              : from === 'pm'
                ? t('transfer.amount_helper_pm')
                : t('transfer.amount_helper_main')
        }
      />

      {fee > 0 && amt > 0 ? (
        <PreviewBox>
          <PreviewRow
            label={t('transfer.preview_lands')}
            value={formatMoney(amt, locale)}
            strong
          />
          <PreviewRow
            label={t('transfer.preview_fee_hl')}
            value={`+${formatMoney(fee, locale)}`}
            tone="text-tg-hint"
          />
          <div className="my-1.5 h-px bg-pb-border-soft" />
          <PreviewRow
            label={t('transfer.preview_debit')}
            value={formatMoney(debit, locale)}
            tone="text-tg-hint"
          />
        </PreviewBox>
      ) : null}

      {error ? <ErrorRow message={error} /> : null}

      <Button
        fullWidth
        leading={<SendIcon size={16} />}
        disabled={!canSubmit || mutation.isPending}
        onClick={submit}
      >
        {mutation.isPending ? t('common.loading') : t('transfer.submit_cta')}
      </Button>
      <p className="pb-caption text-center">{t('transfer.eta')}</p>
    </Card>
  );
}

// ─── Form sub-components ──────────────────────────────────────────

function EndpointSelect({
  label,
  value,
  onChange,
  excluded,
}: {
  label: string;
  value: Endpoint;
  onChange: (v: Endpoint) => void;
  /** Hide this option from the list. Used for the "to" select to
   *  prevent picking the same endpoint as "from". */
  excluded: Endpoint | null;
}) {
  const { t } = useTranslation();
  return (
    <label className="block">
      <div className="pb-eyebrow mb-1.5">{label}</div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as Endpoint)}
        className="pb-focus-ring w-full appearance-none rounded-pb-md border border-pb-border-soft bg-pb-paper px-3 py-2.5 text-[14px] font-medium text-tg-text"
      >
        {ENDPOINTS.filter((e) => e !== excluded).map((e) => (
          <option key={e} value={e}>
            {endpointLabel(e, t)}
          </option>
        ))}
      </select>
    </label>
  );
}

function BalanceRow({
  label,
  amount,
  locale,
  symbol = 'USDC',
}: {
  label: string;
  amount: number;
  locale: string;
  symbol?: string;
}) {
  return (
    <div className="flex items-baseline justify-between rounded-pb-sm bg-pb-paper2 px-3 py-2">
      <span className="pb-caption">{label}</span>
      <span className="pb-numeral text-[16px] text-tg-text">
        <AssetBadge symbol={symbol} size={14} />{' '}
        {amount.toLocaleString(locale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}{' '}
        <span className="text-tg-hint">{symbol}</span>
      </span>
    </div>
  );
}

function AmountField({
  amount,
  setAmount,
  max,
  helper,
}: {
  amount: string;
  setAmount: (s: string) => void;
  max: number;
  helper: string;
}) {
  const { t } = useTranslation();
  return (
    <TextField
      label={t('transfer.amount_label')}
      placeholder="0.00"
      value={amount}
      onChange={(e) => setAmount(e.target.value)}
      inputMode="decimal"
      type="number"
      trailing={
        <button
          type="button"
          onClick={() => setAmount(max.toFixed(2))}
          className="pb-focus-ring mr-1 rounded-pb-sm px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-pb-accent hover:bg-pb-accent-soft"
        >
          {t('transfer.max')}
        </button>
      }
      helper={helper}
    />
  );
}

function PreviewBox({ children }: { children: React.ReactNode }) {
  return <div className="rounded-pb-md bg-pb-paper2 p-3">{children}</div>;
}

function PreviewRow({
  label,
  value,
  tone,
  strong,
}: {
  label: string;
  value: string;
  tone?: string;
  strong?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between py-1">
      <span className="text-[13px] text-tg-hint">{label}</span>
      <span
        className={clsx(
          'pb-numeral text-[16px]',
          strong ? 'text-pb-accent' : tone || 'text-tg-text',
        )}
      >
        {value}
      </span>
    </div>
  );
}

function ErrorRow({ message }: { message: string }) {
  return (
    <p className="rounded-pb-md bg-[color-mix(in_oklab,var(--pb-danger)_14%,transparent)] px-3 py-2 text-[13px] text-pb-danger">
      {message}
    </p>
  );
}

// ─── Route preview row ────────────────────────────────────────────

interface EndpointSummary {
  /** ``null`` for the chain-agnostic Main wallet aggregate. */
  chainId: number | null;
  name: string;
  asset: string;
}

function RouteRow({ from, to }: { from: EndpointSummary; to: EndpointSummary }) {
  return (
    <div className="rounded-pb-sm bg-pb-paper2 px-3 py-2.5">
      <Endpoint chainId={from.chainId} name={from.name} asset={from.asset} />
      <div
        aria-hidden
        className="my-1 ml-1 flex h-4 items-center text-[14px] leading-none text-tg-hint"
      >
        ↓
      </div>
      <Endpoint chainId={to.chainId} name={to.name} asset={to.asset} />
    </div>
  );
}

function Endpoint({
  chainId,
  name,
  asset,
}: {
  chainId: number | null;
  name: string;
  asset: string;
}) {
  return (
    <div className="flex min-w-0 items-center gap-2 text-[13px]">
      {chainId !== null ? <NetworkBadge chainId={chainId} size={28} /> : null}
      <span className="font-medium text-tg-text">{name}</span>
      <span className="text-tg-hint">·</span>
      <AssetBadge symbol={asset} size={14} />
      <span className="text-tg-hint">{asset}</span>
    </div>
  );
}

// ─── History (uses /v1/transfers list — covers the new directions) ─

function HistorySection({
  items,
  locale,
}: {
  items: TransferRowItem[];
  locale: string;
}) {
  const { t } = useTranslation();
  return (
    <section className="mt-8">
      <div className="mb-3 flex items-center justify-between px-1">
        <h2 className="pb-eyebrow">{t('transfer.history')}</h2>
      </div>
      {items.length > 0 ? (
        <Card pad="none" variant="flat">
          <ul className="divide-y divide-pb-border-soft">
            {items.map((it) => (
              <li key={it.id}>
                <HistoryRow item={it} locale={locale} />
              </li>
            ))}
          </ul>
        </Card>
      ) : (
        <Card pad="md" className="text-center">
          <p className="pb-caption">{t('transfer.no_history')}</p>
        </Card>
      )}
    </section>
  );
}

function HistoryRow({
  item,
  locale,
}: {
  item: TransferRowItem;
  locale: string;
}) {
  const { t } = useTranslation();
  const dirLabel = directionLabel(item.direction, t);
  const failed = item.status.startsWith('failed_at_');
  const stateClass = clsx(
    'pb-eyebrow rounded-pb-sm px-2 py-0.5',
    item.status === 'completed' && 'bg-pb-accent-soft text-pb-accent',
    failed &&
      'bg-[color-mix(in_oklab,var(--pb-danger)_14%,transparent)] text-pb-danger',
    item.status !== 'completed' &&
      !failed &&
      'bg-pb-paper2 text-tg-hint',
  );
  const stateLabel = failed
    ? t('transfer.state_failed')
    : item.status === 'completed'
      ? t('transfer.state_done')
      : t('transfer.state_in_progress');
  return (
    <div className="flex items-center justify-between px-3 py-3">
      <div>
        <div className="text-[14px] text-tg-text">{dirLabel}</div>
        <div className="pb-caption">
          {new Date(item.requested_at).toLocaleString(locale)}
        </div>
      </div>
      <div className="text-right">
        <div className="pb-numeral text-[16px] text-tg-text">
          ${Number(item.amount_usdc).toLocaleString(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>
        <span className={stateClass}>{stateLabel}</span>
      </div>
    </div>
  );
}

function directionLabel(direction: string, t: (k: string) => string): string {
  switch (direction) {
    case 'eoa_to_pm':
      return t('transfer.allocate_pm');
    case 'eoa_to_hl':
      return t('transfer.allocate_hl');
    case 'pm_to_eoa':
      return t('transfer.deallocate_pm');
    case 'hl_to_eoa':
      return t('transfer.deallocate_hl');
    case 'pm_to_hl':
      return t('transfer.dir_pm_to_hl');
    case 'hl_to_pm':
      return t('transfer.dir_hl_to_pm');
    default:
      return direction;
  }
}

// ─── Helpers ──────────────────────────────────────────────────────

function endpointLabel(e: Endpoint, t: (k: string) => string): string {
  if (e === 'main') return t('transfer.endpoint_main');
  if (e === 'pm') return t('transfer.bot_pm');
  return t('transfer.bot_hl');
}

function endpointSummary(
  e: Endpoint,
  t: (k: string) => string,
): EndpointSummary {
  if (e === 'main') {
    // Chain-agnostic: backend picks the right source chain based on
    // available balances and bridges if needed.
    return { chainId: null, name: t('transfer.endpoint_main'), asset: 'USDC' };
  }
  if (e === 'pm') {
    return { chainId: 137, name: t('transfer.bot_pm'), asset: 'USDC' };
  }
  return { chainId: 42161, name: t('transfer.bot_hl'), asset: 'USDC' };
}

/** Single-token balance on the main wallet for a given chain. Missing
 *  token (or no balances loaded yet) reads as 0. */
function tokenBalanceOnChain(
  balances: MyBalancesResponse | undefined,
  chain: Chain,
  symbol: string,
): number {
  if (!balances) return 0;
  const target: BalancesChainBalance =
    chain === 'polygon'
      ? balances.main_wallet.polygon
      : balances.main_wallet.arbitrum;
  return Number(target.tokens.find((t) => t.symbol === symbol)?.amount ?? 0);
}

function formatMoney(n: number, locale: string): string {
  return `$${n.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
